class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    this.addEventListener('click', (evt) => {
      const btn = evt.target.closest('#CartDrawer-Checkout');
      if (btn && btn.dataset.checkoutBlocked === 'true') {
        evt.preventDefault();
        this.close();
      }
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const responseKey = section.section || section.id;

      if (!sectionElement) return;
      if (!parsedState.sections?.[responseKey]) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[responseKey], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    const sections = [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
    const barWrapper = document.querySelector('.bottom-purchase-bar-section')?.closest('[id^="shopify-section-"]');
    if (barWrapper) {
      const barSectionId = barWrapper.id.replace('shopify-section-', '');
      sections.push({
        id: barWrapper.id,
        section: barSectionId,
        selector: '#' + barWrapper.id,
      });
    }
    return sections;
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    const sections = [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
    const barWrapper = document.querySelector('.bottom-purchase-bar-section')?.closest('[id^="shopify-section-"]');
    if (barWrapper) {
      const barSectionId = barWrapper.id.replace('shopify-section-', '');
      sections.push({
        id: barWrapper.id,
        section: barSectionId,
        selector: '#' + barWrapper.id,
      });
    }
    return sections;
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/**
 * When visiting the cart page (/cart) or step-3 summary pages, open the cart drawer
 * automatically so the user gets the drawer UX.
 */
function openCartDrawerOnCartPage() {
  const pathname = window.location.pathname.replace(/\/$/, '');
  const autoOpenPaths = [
    '/cart',
    '/pages/subscription-step-3',
  ];
  if (!autoOpenPaths.includes(pathname)) return;
  const drawer = document.querySelector('cart-drawer');
  if (drawer) {
    requestAnimationFrame(() => drawer.open());
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', openCartDrawerOnCartPage);
} else {
  openCartDrawerOnCartPage();
}
